import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Heading,
  useToast,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import ApiResponse from "../../../services/ApiResponse";
import { Noticia } from "../../../services/models/Noticia";
import handleApiError from "../../../utils/ApiErrorHandler";
import { ApiValidationResponse } from "../../../services/models/ApiValidationResponse";

const NoticiaEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [noticia, setNoticia] = useState<Noticia | null>(
    (location.state as Noticia) || null
  );
  const [loading, setLoading] = useState(false);

  const tituloRef = useRef<HTMLInputElement>(null);
  const resumenRef = useRef<HTMLInputElement>(null);
  const contenidoRef = useRef<HTMLTextAreaElement>(null);
  const imagenRef = useRef<HTMLInputElement>(null);
  const visibilidadRef = useRef<HTMLSelectElement>(null);
  const estadoRef = useRef<HTMLSelectElement>(null);
  const fechaPublicacionRef = useRef<HTMLInputElement>(null);

  /* ================================
     Cargar noticia si no viene por state
  ================================= */
  useEffect(() => {
    if (!id) {
      toast({
        title: "ID inválido",
        status: "error",
      });
      navigate("/dashboard/noticias");
      return;
    }

    if (!noticia) {
      setLoading(true);
      const apiResponse = new ApiResponse<Noticia>();

      apiResponse.useFetch(`noticias/${id}`, "GET").then(() => {
        if (!apiResponse.error) {
          setNoticia(apiResponse.data as Noticia);
        } else {
          toast({
            title: "Error",
            description: "No se pudo cargar la noticia.",
            status: "error",
          });
          navigate("/dashboard/noticias");
        }
        setLoading(false);
      });
    }
  }, [id, noticia, navigate, toast]);

  if (loading || !noticia) {
    return (
      <Box textAlign="center" mt={10}>
        <Spinner size="xl" />
      </Box>
    );
  }

  const storedDate = noticia.fecha_publicacion
    ? noticia.fecha_publicacion.split("T")[0]
    : "";

  /* ================================
     Submit
  ================================= */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    const fecha = fechaPublicacionRef.current?.value || null;
    const formattedFecha = fecha ? `${fecha} 00:00:00` : null;

    const formData = {
      titulo: tituloRef.current?.value || "",
      resumen: resumenRef.current?.value || "",
      contenido: contenidoRef.current?.value || "",
      imagen: imagenRef.current?.value || null,
      visibilidad: visibilidadRef.current?.value,
      estado: Number(estadoRef.current?.value), // 👈 NUMÉRICO
      fecha_publicacion: formattedFecha,
    };

    const apiResponse = new ApiResponse<Noticia>();
    await apiResponse.useFetch(`noticias/${id}`, "PATCH", formData);

    if (!apiResponse.error) {
      toast({
        title: "Noticia actualizada",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/dashboard/noticias");
    } else if (
      typeof apiResponse.error === "object" &&
      apiResponse.error !== null &&
      "errors" in apiResponse.error
    ) {
      handleApiError(apiResponse.error as ApiValidationResponse, toast);
    } else {
      toast({
        title: "Error",
        description: "No se pudo actualizar la noticia.",
        status: "error",
      });
    }
  };

  /* ================================
     Render
  ================================= */
  return (
    <Box maxW="lg" mx="auto" mt={8} p={6} borderWidth={1} borderRadius="lg">
      <Heading mb={6} textAlign="center">
        Editar Noticia
      </Heading>

      <form onSubmit={handleSubmit}>
        <VStack spacing={5}>
          <FormControl isRequired>
            <FormLabel>Título</FormLabel>
            <Input ref={tituloRef} defaultValue={noticia.titulo} />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Resumen</FormLabel>
            <Input ref={resumenRef} defaultValue={noticia.resumen ?? ""} />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Contenido</FormLabel>
            <Textarea
              ref={contenidoRef}
              rows={6}
              defaultValue={noticia.contenido}
            />
          </FormControl>

          <FormControl>
            <Image
              src={
                noticia.imagen ||
                "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR4Ni5wwO02H47WSTi7PG2V-WuYZUXTfXGpmw&s"
              }
              mt={2}
              boxSize="150px"
              objectFit="cover"
              borderRadius="md"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Visibilidad</FormLabel>
            <Select
              ref={visibilidadRef}
              defaultValue={noticia.visibilidad}
            >
              <option value="PUBLICA">Pública</option>
              <option value="INSTITUCIONAL">Institucional</option>
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Estado</FormLabel>
            <Select
              ref={estadoRef}
              defaultValue={noticia.estado} //ya es number
            >
              <option value={0}>Borrador</option>
              <option value={1}>Publicada</option>
              <option value={2}>Archivada</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Fecha de publicación</FormLabel>
            <Input
              type="date"
              ref={fechaPublicacionRef}
              defaultValue={storedDate}
            />
          </FormControl>

          <Button type="submit" colorScheme="blue" width="full">
            Guardar Cambios
          </Button>
        </VStack>
      </form>
    </Box>
  );
};

export default NoticiaEditPage;
